import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import backgroundPattern from "../../assets/pasbookpng.png";
import CustomAlert from "../../components/common/CustomAlert";
import postalApi from "../../api/postalApi"; 
import vendorApi from "../../api/vendorApi";
import { BASE_URL, API_ENDPOINTS } from "../../config/apiconfig";
import "./vendorform.css";

/* =
   COUNTRIES
= */

const countries = [
  "India",
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "France",
  "Japan",
  "China",
  "Singapore",
  "United Arab Emirates",
  "Saudi Arabia",
  "New Zealand",
  "Nepal",
  "Sri Lanka",
];

const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

/* =
   SEARCHABLE STATE DROPDOWN
= */

function SearchableStateDropdown({
  name,
  value,
  onChange,
  label = "State",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredStates = indianStates.filter((state) =>
    state.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (state) => {
    onChange({
      target: {
        name,
        value: state,
      },
    });
    setIsOpen(false);
    setSearch("");
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  return (
    <div className="form-group" ref={dropdownRef}>
      <label htmlFor={name}>{label}</label>
      <div className="salutation-dropdown">
        <button
          type="button"
          className="salutation-dropdown-button"
          onClick={() => {
            if (isOpen) {
              setIsOpen(false);
              setSearch("");
            } else {
              setIsOpen(true);
            }
          }}
        >
          <span className="salutation-value">
            {value || "Select State"}
          </span>
          <span className="dropdown-arrow">▼</span>
        </button>

        {isOpen && (
          <div className="salutation-dropdown-menu">
            <div className="salutation-search-wrapper">
              <input
                ref={searchInputRef}
                type="text"
                className="salutation-search"
                placeholder="Search state..."
                value={search}
                onChange={handleSearchChange}
                autoFocus
              />
            </div>

            <div className="salutation-list">
              {filteredStates.length > 0 ? (
                filteredStates.map((state) => (
                  <div
                    key={state}
                    className={`salutation-option ${
                      value === state ? "selected" : ""
                    }`}
                    onClick={() => handleSelect(state)}
                  >
                    <span>{state}</span>
                    {value === state && (
                      <span className="salutation-check">✓</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-salutation">No state found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =
   SEARCHABLE COUNTRY DROPDOWN
= */

function SearchableCountryDropdown({
  name,
  value,
  onChange,
  label = "Country",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const filteredCountries = countries.filter((country) =>
    country.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpen = () => {
    setIsOpen(true);

    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  };

  const handleSelect = (country) => {
    onChange({
      target: {
        name,
        value: country,
      },
    });

    setIsOpen(false);
    setSearch("");
  };

  return (
    <div className="form-group">
      <label>{label}</label>

      <div className="country-dropdown" ref={dropdownRef}>
        <button
          type="button"
          className="country-dropdown-button"
          onClick={() => {
            if (isOpen) {
              setIsOpen(false);
              setSearch("");
            } else {
              handleOpen();
            }
          }}
        >
          <span>{value || "Select country"}</span>
          <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
        </button>

        {isOpen && (
          <div className="country-dropdown-menu">
            <div className="country-search-wrapper">
              <input
                ref={searchInputRef}
                type="text"
                className="country-search"
                placeholder="Search country..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className="country-list">
              {filteredCountries.length > 0 ? (
                filteredCountries.map((country) => (
                  <div
                    key={country}
                    className={`country-option ${
                      value === country ? "selected" : ""
                    }`}
                    onClick={() => handleSelect(country)}
                  >
                    {country}
                    {value === country && (
                      <span className="country-check">✓</span>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-country">No country found</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =
   SALUTATIONS
= */

const salutations = ["Mr.", "Mrs.", "Ms.", "Miss","Dr."];

/* =
   DISPLAY NAME DROPDOWN
= */

function DisplayNameDropdown({ value, suggestions, error, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleSelect = (suggestion) => {
    onChange({
      target: {
        name: "displayName",
        value: suggestion,
      },
    });
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="form-group display-name-group" ref={dropdownRef}>
      <label htmlFor="displayName">
        Display Name
      </label>

      <div className="display-name-dropdown">
        <div className="display-name-input-wrapper">
          <input
            id="displayName"
            name="displayName"
            type="text"
            placeholder="Enter or select display name"
            value={value}
            onChange={(e) => {
              onChange(e);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            autoComplete="off"
            className={error ? "error-input" : ""}
          />

          <button
            type="button"
            className={`display-name-arrow ${
              isOpen ? "open" : ""
            }`}
            onClick={() => setIsOpen((prev) => !prev)}
          >
            ▼
          </button>
        </div>

        {isOpen && suggestions.length > 0 && (
          <div className="display-name-dropdown-menu">
            <div className="display-name-dropdown-title">
              Suggested Display Names
            </div>

            <div className="display-name-suggestion-list">
              {suggestions.map((suggestion) => (
                <button
                  type="button"
                  key={suggestion}
                  className={`display-name-suggestion ${
                    value === suggestion ? "selected" : ""
                  }`}
                  onClick={() => handleSelect(suggestion)}
                >
                  <span>{suggestion}</span>

                  {value === suggestion && (
                    <span className="display-name-check">
                      ✓
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <small className="error-message">
          {error}
        </small>
      )}
    </div>
  );
}

/* =
   PRIMARY CONTACT DROPDOWN
= */

function PrimaryContactDropdown({
  value,
  onChange,
  label = "Primary Contact",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleSelect = (salutation) => {
    onChange(salutation);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="form-group salutation-group" ref={dropdownRef}>
      <label>{label}</label>

      <div className="salutation-dropdown">
        <button
          type="button"
          className="salutation-dropdown-button"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className="salutation-value">
            {value || "Select"}
          </span>
          <span className={`dropdown-arrow ${isOpen ? "open" : ""}`}>
            ▼
          </span>
        </button>

        {isOpen && (
          <div className="salutation-dropdown-menu">
            <div className="salutation-list">
              {salutations.map((salutation) => (
                <div
                  key={salutation}
                  className={`salutation-option ${
                    value === salutation ? "selected" : ""
                  }`}
                  onClick={() => handleSelect(salutation)}
                >
                  <span>{salutation}</span>
                  {value === salutation && (
                    <span className="salutation-check">✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* =
   VENDOR FORM
= */

function VendorForm() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're in edit mode
  const editMode = location.state?.editMode || false;
  const editVendor = location.state?.vendor || null;

  const [errors, setErrors] = useState({});
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [gstLoading] = useState(false);
  const [gstError, setGstError] = useState("");
  const [postalLoading, setPostalLoading] = useState({ billing: false, shipping: false });
  const [postalError, setPostalError] = useState({ billing: "", shipping: "" });
  const postalRequestRef = useRef({ billing: "", shipping: "" });
  const [backgroundSize, setBackgroundSize] = useState('220px');
  const [customAlert, setCustomAlert] = useState({ isOpen: false, message: '', type: 'info' });

  // Product-wise financial preference picker
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [newRowActionType, setNewRowActionType] = useState("Inward");
  const [newRowOpeningQty, setNewRowOpeningQty] = useState("");
  const productMenuRef = useRef(null);

  const showAlert = (message, type = 'info') => {
    setCustomAlert({ isOpen: true, message, type });
  };

  // Responsive background size
  useEffect(() => {
    const updateBackgroundSize = () => {
      if (window.innerWidth <= 480) {
        setBackgroundSize('120px');
      } else if (window.innerWidth <= 768) {
        setBackgroundSize('150px');
      } else {
        setBackgroundSize('220px');
      }
    };

    updateBackgroundSize();
    window.addEventListener('resize', updateBackgroundSize);
    return () => window.removeEventListener('resize', updateBackgroundSize);
  }, []);

  /* ======
     FORM DATA - Initialize with edit data if available
  ====== */

  const [formData, setFormData] = useState(() => {
    if (editMode && editVendor) {
      // Pre-fill with vendor data for editing
      return {
        gstin: editVendor.gstin || "",
        salutation: editVendor.salutation || "",
        firstName: editVendor.firstName || "",
        lastName: editVendor.lastName || "",
        primaryContact: editVendor.primaryContact || "",
        companyName: editVendor.companyName || "",
        displayName: editVendor.displayName || "",
        email: editVendor.email || "",
        phone: editVendor.phone || "",

        billingCountry: editVendor.billingCountry || "India",
        billingAddress: editVendor.billingAddress || "",
        billingCity: editVendor.billingCity || "",
        billingState: editVendor.billingState || "",
        billingPinCode: editVendor.billingPinCode || "",
        billingPhone: editVendor.billingPhone || "",
        billingFax: editVendor.billingFax || "",

        shippingAttention: editVendor.shippingAttention || "",
        shippingCountry: editVendor.shippingCountry || "India",
        shippingAddress: editVendor.shippingAddress || "",
        deliveryAddress: editVendor.deliveryAddress || "",
        shippingCity: editVendor.shippingCity || "",
        shippingState: editVendor.shippingState || "",
        shippingPinCode: editVendor.shippingPinCode || "",
        shippingPhone: editVendor.shippingPhone || "",
        shippingFax: editVendor.shippingFax || "",

        openingBalance: editVendor.openingBalance || "",
        depositAmount: editVendor.depositAmount || "",
        products: Array.isArray(editVendor.products || editVendor.productPreferences)
          ? (editVendor.products || editVendor.productPreferences).map((item) => ({
              id: item.id ?? item.productId,
              name: item.name ?? item.productName ?? item.product?.name ?? "",
              actionType:
                item.actionType ||
                (item.action === "Rec" || item.action === "Received" ? "Inward" :
                 item.action === "Del" || item.action === "Delivered" ? "Outward" : item.action) ||
                "Inward",
              openingQty: Number(item.openingQty ?? item.qty ?? item.openingBalance ?? 0),
            }))
          : [],
      };
    }
    // Default empty form for adding
    return {
      gstin: "",
      salutation: "",
      firstName: "",
      lastName: "",
      primaryContact: "",
      companyName: "",
      displayName: "",
      email: "",
      phone: "",

      billingCountry: "India",
      billingAddress: "",
      billingCity: "",
      billingState: "",
      billingPinCode: "",
      billingPhone: "",
      billingFax: "",

      shippingAttention: "",
      shippingCountry: "India",
      shippingAddress: "",
      deliveryAddress: "",
      shippingCity: "",
      shippingState: "",
      shippingPinCode: "",
      shippingPhone: "",
      shippingFax: "",

      openingBalance: "",
      depositAmount: "",
      products: [],
    };
  });

  /* ======
     PRODUCT-WISE FINANCIAL PREFERENCES
  ====== */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (productMenuRef.current && !productMenuRef.current.contains(event.target)) {
        setIsProductMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await fetch(`${BASE_URL}${API_ENDPOINTS.GOODS.GET_ALL}`, {
          method: "GET",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" },
        });
        if (!response.ok) throw new Error("Failed to fetch products");
        const data = await response.json();
        const catalog = Array.isArray(data) ? data : data?.goods || data?.products || data?.data || [];
        setProducts(catalog.map((item) => ({
          id: item.id ?? item.productId,
          name: item.name ?? item.productName ?? item.product?.name ?? "",
        })).filter((item) => item.id != null && item.name));
      } catch (error) {
        console.error("Error fetching products for vendor:", error);
        setProducts([]);
        showAlert("Unable to load products. Please try again.", "error");
      } finally {
        setLoadingProducts(false);
      }
    };
    fetchProducts();
  }, []);

  const availableProducts = products.filter((product) =>
    !(formData.products || []).some((row) => String(row.id) === String(product.id)) &&
    product.name.toLowerCase().includes(productSearchTerm.toLowerCase().trim())
  );

  const handleSelectProduct = (product) => {
    setSelectedProductId(product.id);
    setProductSearchTerm(product.name);
    setIsProductMenuOpen(false);
  };

  const handleAddProductRow = () => {
    const product = products.find((item) => String(item.id) === String(selectedProductId));
    if (!product) {
      showAlert("Select a product to add.", "error");
      return;
    }

    const alreadyAdded = (formData.products || []).some(
      (item) => String(item.id) === String(product.id)
    );

    if (alreadyAdded) {
      showAlert("This product has already been added. Select another product.", "error");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      products: [
        ...(prev.products || []),
        {
          id: product.id,
          name: product.name,
          actionType: newRowActionType,
          openingQty: Number(newRowOpeningQty) || 0,
        },
      ],
    }));

    // Clear only the new-product inputs so another product can be selected immediately.
    setSelectedProductId(null);
    setProductSearchTerm("");
    setNewRowActionType("Inward");
    setNewRowOpeningQty("");
    setIsProductMenuOpen(false);
  };

  const handleProductPreferenceChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      products: (prev.products || []).map((product, productIndex) => productIndex === index ? { ...product, [field]: value } : product),
    }));
  };

  const handleRemoveProductRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      products: (prev.products || []).filter((_, productIndex) => productIndex !== index),
    }));
  };

  const displayNameSuggestions = [
    // Primary Contact (First Name + Last Name)
    [formData.firstName, formData.lastName]
      .filter(Boolean)
      .join(" ")
      .trim(),

    // Last Name, First Name
    formData.firstName?.trim() &&
    formData.lastName?.trim()
      ? `${formData.lastName.trim()}, ${formData.firstName.trim()}`
      : "",

    // Company Name
    formData.companyName?.trim(),
  ]
    .filter(Boolean)
    .filter(
      (item, index, array) =>
        array.indexOf(item) === index
    );

  /* ======
     VALIDATION
  ====== */

  const validateForm = () => {
    let newErrors = {};

    // Deposit Amount validation
    if (formData.depositAmount !== "" && formData.depositAmount !== null && formData.depositAmount !== undefined) {
      if (isNaN(formData.depositAmount) || Number(formData.depositAmount) < 0) {
        newErrors.depositAmount = "Deposit Amount must be a positive number";
      }
    }

    // GSTIN validation
    if (formData.gstin.trim() && formData.gstin.length !== 15) {
      newErrors.gstin = "GSTIN must be exactly 15 characters";
    }

    // Company Name validation
    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company Name is required";
    }


    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Phone validation
    if (!formData.phone) {
      newErrors.phone = "Phone is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Phone must be exactly 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ======
     HANDLE GSTIN CHANGE
  ====== */

  const handleGSTChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value.toUpperCase(), // Auto-uppercase GSTIN
    }));

    // Clear GST error when user types
    if (errors.gstin) {
      setErrors((prev) => ({
        ...prev,
        gstin: "",
      }));
    }
    setGstError("");
  };

  /* ======
     PIN CODE AUTO-FILL
     Uses the free India postal pincode API to populate
     city/district, state and country from a 6-digit PIN.
  ====== */

  const fetchAddressByPincode = async (addressType, pincode) => {
    const cleanPin = String(pincode || "").replace(/\D/g, "").slice(0, 6);

    if (cleanPin.length !== 6) {
      return;
    }

    // Avoid requesting the same PIN repeatedly for the same address.
    if (postalRequestRef.current[addressType] === cleanPin) {
      return;
    }

    postalRequestRef.current[addressType] = cleanPin;
    setPostalLoading((prev) => ({ ...prev, [addressType]: true }));
    setPostalError((prev) => ({ ...prev, [addressType]: "" }));

    try {
      const result = await postalApi.getByPincode(cleanPin);

      if (!result?.success) {
        throw new Error(result?.message || "Pincode not found");
      }

      const prefix = addressType === "billing" ? "billing" : "shipping";

      setFormData((prev) => ({
        ...prev,
        [`${prefix}City`]: result.city || prev[`${prefix}City`],
        [`${prefix}State`]: result.state || prev[`${prefix}State`],
        [`${prefix}Country`]: result.country || "India",
        [`${prefix}PinCode`]: cleanPin,
      }));

      setPostalError((prev) => ({ ...prev, [addressType]: "" }));
    } catch (error) {
      console.error(`Pincode lookup failed for ${addressType}:`, error);
      setPostalError((prev) => ({
        ...prev,
        [addressType]: error.message || "Pincode not found. Please enter the address manually.",
      }));
    } finally {
      setPostalLoading((prev) => ({ ...prev, [addressType]: false }));
    }
  };

  const handlePincodeChange = (e, addressType) => {
    const cleanValue = e.target.value.replace(/\D/g, "").slice(0, 6);
    const fieldName = addressType === "billing" ? "billingPinCode" : "shippingPinCode";

    // Allow a new lookup when the user changes the PIN.
    postalRequestRef.current[addressType] = "";
    setPostalError((prev) => ({ ...prev, [addressType]: "" }));

    setFormData((prev) => ({
      ...prev,
      [fieldName]: cleanValue,
    }));

    // Automatically lookup as soon as a complete 6-digit PIN is entered.
    if (cleanValue.length === 6) {
      fetchAddressByPincode(addressType, cleanValue);
    }
  };

  /* ======
     HANDLE INPUT CHANGE
  ====== */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value,
      };

      if (
        name === "firstName" ||
        name === "lastName"
      ) {
        const firstName = name === "firstName" ? value : prev.firstName;
        const lastName = name === "lastName" ? value : prev.lastName;

        updatedData.primaryContact = [firstName, lastName]
          .filter(Boolean)
          .join(" ");
      }

      // Clear error for this field when user types
      if (errors[name]) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [name]: "",
        }));
      }

      return updatedData;
    });
  };

  /* ======
     COPY BILLING ADDRESS TO SHIPPING
  ====== */

  const handleCopyBilling = (e) => {
    const checked = e.target.checked;
    setSameAsBilling(checked);

    if (checked) {
      setFormData((prev) => ({
        ...prev,
        shippingAttention: prev.primaryContact,
        shippingCountry: prev.billingCountry,
        shippingAddress: prev.billingAddress,
        shippingCity: prev.billingCity,
        shippingState: prev.billingState,
        shippingPinCode: prev.billingPinCode,
        shippingPhone: prev.billingPhone,
        shippingFax: prev.billingFax,
      }));
    }
  };

  /* ======
     CANCEL FORM
  ====== */

  const handleCancel = () => {
    setFormData({
      gstin: "",
      salutation: "",
      firstName: "",
      lastName: "",
      primaryContact: "",
      companyName: "",
      displayName: "",
      email: "",
      phone: "",

      billingCountry: "India",
      billingAddress: "",
      billingCity: "",
      billingState: "",
      billingPinCode: "",
      billingPhone: "",
      billingFax: "",

      shippingAttention: "",
      shippingCountry: "India",
      shippingAddress: "",
      deliveryAddress: "",
      shippingCity: "",
      shippingState: "",
      shippingPinCode: "",
      shippingPhone: "",
      shippingFax: "",

      openingBalance: "",
      depositAmount: "",
      products: [],
    });

    setProductSearchTerm("");
    setSelectedProductId(null);
    setNewRowActionType("Inward");
    setNewRowOpeningQty("");
    setIsProductMenuOpen(false);
    setSameAsBilling(false);
    setErrors({});
    setGstError("");
    navigate("/dashboard/vendors/view");
  };

  /* ======
     HANDLE SUBMIT - Supports both Add and Edit
  ====== */

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Run validation first
    if (!validateForm()) {
      const firstError = document.querySelector(".error-message");
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Phone validation
    const phoneFields = [
      { value: formData.phone, name: "Vendor Phone" },
      { value: formData.billingPhone, name: "Billing Phone" },
      { value: formData.shippingPhone, name: "Shipping Phone" },
    ];

    const invalidPhone = phoneFields.find(
      (field) => field.value && field.value.length !== 10
    );

    if (invalidPhone) {
      showAlert(`${invalidPhone.name} must contain exactly 10 digits.`, "error");
      return;
    }

    const vendorData = {
      gstin: formData.gstin,
      salutation: formData.salutation,
      firstName: formData.firstName,
      lastName: formData.lastName,
      primaryContact: formData.primaryContact,
      companyName: formData.companyName,
      displayName: formData.displayName,
      email: formData.email,
      phone: formData.phone,
      billingAddress: formData.billingAddress,
      billingCountry: formData.billingCountry,
      billingCity: formData.billingCity,
      billingState: formData.billingState,
      billingPinCode: formData.billingPinCode,
      billingPhone: formData.billingPhone,
      billingFax: formData.billingFax,
      shippingAttention: formData.shippingAttention,
      shippingCountry: formData.shippingCountry,
      shippingAddress: formData.shippingAddress,
      deliveryAddress: formData.deliveryAddress,
      shippingCity: formData.shippingCity,
      shippingState: formData.shippingState,
      shippingPinCode: formData.shippingPinCode,
      shippingPhone: formData.shippingPhone,
      shippingFax: formData.shippingFax,
      depositAmount: Number(formData.depositAmount) || 0,
      products: (formData.products || []).map((product) => ({
        id: product.id,
        name: product.name,
        actionType: product.actionType,
        openingQty: Number(product.openingQty) || 0,
      })),
      name: formData.companyName || formData.displayName || formData.primaryContact,
      mobile: formData.phone || formData.primaryContact,
      address: formData.billingAddress,
    };

    try {
      if (editMode && editVendor) {
        await vendorApi.update(editVendor.id, vendorData);
      } else { 
        await vendorApi.create(vendorData);
      }

      // On success, show alert and navigate
      showAlert(editMode ? "Vendor updated successfully!" : "Vendor saved successfully!", "success");
      
      // Optional: Clear local cache after a successful save/update to force a fresh fetch
      // on the next table load. This ensures the table shows the latest data.
      localStorage.removeItem("vendorList");

      // Using a timeout to allow the user to see the success message before navigating
      setTimeout(() => {
        navigate("/dashboard/vendors/view");
      }, 1500);

    } catch (err) {
      console.error(editMode ? "Error updating vendor:" : "Error saving vendor:", err);
      showAlert(
        editMode
          ? `Update failed: ${err.message}`
          : `Save failed: ${err.message}`,
        "error"
      );
    }
  };

  /* ======
     RETURN
  ====== */

  return (
    <div className="page">
      <div
        style={{
          position: 'fixed',
          inset: '-30%',
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: 'repeat',
          backgroundSize: backgroundSize,
          transform: 'rotate(-12deg)',
          opacity: 0.08,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div className="vendor-container" style={{ position: 'relative', zIndex: 1 }}>
        {/* PAGE HEADER */}
        <div className="page-header-section">
          <div className="header-title-group">
            <div className="header-icon">
              <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="24" width="24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <h2>{editMode ? "Edit Vendor" : "Vendor Form"}</h2>
            <button
              type="button"
              className="back-button"
              onClick={() => navigate("/dashboard/vendors/view")}
            >
              <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="20" width="20" xmlns="http://www.w3.org/2000/svg">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>
          </div>
          <p>
            {editMode
              ? "Update vendor information"
              : "Add a new vendor to your organization"}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* GST DETAILS - Section 1 */}
          <section className="form-section">
            <div className="section-header">
              <div className="section-number">1</div>
              <div>
                <h2>GST Details</h2>
                <p>Enter GSTIN and click "Prefill Details" to auto-fetch vendor information</p>
              </div>
            </div>

            <div className="form-grid gst-grid">
              <div className="form-group gst-input-group">
                <label htmlFor="gstin">
                  GSTIN
                </label>
                <div className="gst-input-wrapper">
                  <input
                    id="gstin"
                    name="gstin"
                    type="text"
                    placeholder="Enter 15-digit GSTIN"
                    value={formData.gstin}
                    onChange={handleGSTChange}
                    className={errors.gstin || gstError ? "error-input" : ""}
                    maxLength="15"
                    disabled={editMode} // Disable GSTIN editing in edit mode
                  />
                  <button
                    type="button"
                    className="btn btn-secondary gst-fetch-btn"
                    onClick={() => { /* Prefill logic removed */ }}
                    disabled={true || gstLoading || editMode}
                  >
                    {gstLoading ? " Loading..." : " Prefill Details"}
                  </button>
                </div>
                {errors.gstin && (
                  <small className="error-message">{errors.gstin}</small>
                )}
                {gstError && !errors.gstin && (
                  <small className="error-message">{gstError}</small>
                )}
                {editMode && (
                  <small className="info-message">
                    ⚠️ GSTIN cannot be edited in edit mode
                  </small>
                )}
              </div>
            </div>
          </section>

          {/* VENDOR INFORMATION - Section 2 */}
          <section className="form-section">
            <div className="section-header">
              <div className="section-number">2</div>
              <div>
                <h2>Vendor Information</h2>
                <p>Basic information about the vendor</p>
              </div>
            </div>

            <div className="form-grid">
              <div className="primary-contact-wrapper">
                <div className="primary-contact-grid">
                  <PrimaryContactDropdown
                    value={formData.salutation}
                    onChange={(value) =>
                      handleChange({
                        target: {
                          name: "salutation",
                          value,
                        },
                      })
                    }
                    label="Primary Contact"
                  />

                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="companyName">
                  Company Name <span className="required">*</span>
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="Enter company name"
                  value={formData.companyName}
                  onChange={handleChange}
                  className={errors.companyName ? "error-input" : ""}
                />
                {errors.companyName && (
                  <small className="error-message">{errors.companyName}</small>
                )}
              </div>

              <DisplayNameDropdown
                value={formData.displayName}
                suggestions={displayNameSuggestions}
                error={errors.displayName}
                onChange={handleChange}
              />

              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? "error-input" : ""}
                />
                {errors.email && (
                  <small className="error-message">{errors.email}</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone">
                  Phone <span className="required">*</span>
                </label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  placeholder="Enter phone number"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? "error-input" : ""}
                />
                {errors.phone && (
                  <small className="error-message">{errors.phone}</small>
                )}
              </div>
            </div>
          </section>

          {/* =
              ADDRESS DETAILS - BILLING + SHIPPING
= */}
          <div className="address-layout">

          {/* BILLING ADDRESS - Section 3 */}
          <section className="form-section address-section">
            <div className="section-header">
              <div className="section-number">3</div>
              <div>
                <h2>Billing Address</h2>
                <p>Vendor's billing address</p>
              </div>
            </div>

            <div className="form-grid one-column">
              <SearchableCountryDropdown
                name="billingCountry"
                value={formData.billingCountry}
                label="Select Country"
                onChange={handleChange}
              />

              <div className="form-group">
                <label htmlFor="billingAddress">Billing Address</label>
                <textarea
                  id="billingAddress"
                  name="billingAddress"
                  rows="3"
                  placeholder="Enter billing address"
                  value={formData.billingAddress}
                  onChange={handleChange}
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="billingCity">City</label>
                  <input
                    id="billingCity"
                    name="billingCity"
                    type="text"
                    placeholder="City"
                    value={formData.billingCity}
                    onChange={handleChange}
                  />
                </div>

                <SearchableStateDropdown
                  name="billingState"
                  value={formData.billingState}
                  onChange={handleChange}
                  label="State"
                />
              </div>

              <div className="form-group">
                <label htmlFor="billingPinCode">Pin Code</label>
                <input
                  id="billingPinCode"
                  name="billingPinCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit PIN code"
                  value={formData.billingPinCode}
                  onChange={(e) => handlePincodeChange(e, "billing")}
                  onBlur={(e) => fetchAddressByPincode("billing", e.target.value)}
                />
                {postalLoading.billing && (
                  <small className="pincode-status loading">Fetching city & state...</small>
                )}
                {postalError.billing && (
                  <small className="pincode-status error">{postalError.billing}</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="billingPhone">Phone</label>
                <input
                  id="billingPhone"
                  name="billingPhone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  placeholder="Phone"
                  value={formData.billingPhone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="billingFax">Fax Number</label>
                <input
                  id="billingFax"
                  name="billingFax"
                  type="text"
                  placeholder="Fax number"
                  value={formData.billingFax}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>

          {/* SHIPPING ADDRESS - Section 4 */}
          <section className="form-section address-section">
            <div className="section-header shipping-header">
              <div className="section-number">4</div>
              <div className="shipping-title">
                <div>
                  <h2>Shipping Address</h2>
                  <p>Vendor's shipping address</p>
                </div>

                <label className="copy-checkbox">
                  <input
                    type="checkbox"
                    checked={sameAsBilling}
                    onChange={handleCopyBilling}
                  />
                  <span>Copy billing address</span>
                </label>
              </div>
            </div>

            <div className="form-grid one-column">
              <SearchableCountryDropdown
                name="shippingCountry"
                label="Select Country"
                value={formData.shippingCountry}
                onChange={handleChange}
              />

              <div className="form-group">
                <label htmlFor="shippingAddress">Shipping Address</label>
                <textarea
                  id="shippingAddress"
                  name="shippingAddress"
                  rows="3"
                  placeholder="Enter shipping address"
                  value={formData.shippingAddress}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="deliveryAddress">Delivery Address</label>
                <textarea
                  id="deliveryAddress"
                  name="deliveryAddress"
                  rows="3"
                  placeholder="Enter delivery address"
                  value={formData.deliveryAddress}
                  onChange={handleChange}
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label htmlFor="shippingCity">City</label>
                  <input
                    id="shippingCity"
                    name="shippingCity"
                    type="text"
                    placeholder="City"
                    value={formData.shippingCity}
                    onChange={handleChange}
                  />
                </div>

                <SearchableStateDropdown
                  name="shippingState"
                  value={formData.shippingState}
                  onChange={handleChange}
                  label="State"
                />
              </div>

              <div className="form-group">
                <label htmlFor="shippingPinCode">Pin Code</label>
                <input
                  id="shippingPinCode"
                  name="shippingPinCode"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="6-digit PIN code"
                  value={formData.shippingPinCode}
                  onChange={(e) => handlePincodeChange(e, "shipping")}
                  onBlur={(e) => fetchAddressByPincode("shipping", e.target.value)}
                />
                {postalLoading.shipping && (
                  <small className="pincode-status loading">Fetching city & state...</small>
                )}
                {postalError.shipping && (
                  <small className="pincode-status error">{postalError.shipping}</small>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="shippingPhone">Phone</label>
                <input
                  id="shippingPhone"
                  name="shippingPhone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  pattern="[0-9]{10}"
                  placeholder="Phone"
                  value={formData.shippingPhone}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="shippingFax">Fax Number</label>
                <input
                  id="shippingFax"
                  name="shippingFax"
                  type="text"
                  placeholder="Fax number"
                  value={formData.shippingFax}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>

          </div>

          {/* FINANCIAL DETAILS - Section 5 */}
          <section className="form-section amount-section">
            <div className="section-header">
              <div className="section-number">5</div>
              <div><h2>Financial Details</h2><p>Product-wise opening inventory preference for this vendor</p></div>
            </div>

            <div className="vendor-deposit-card">
              <div className="form-group vendor-deposit-field">
                <label htmlFor="depositAmount">
                  Deposit Amount
                </label>
                <div className="input-with-currency">
                  <span className="currency-prefix">INR</span>
                  <input
                    id="depositAmount"
                    name="depositAmount"
                    type="text"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={formData.depositAmount}
                    onChange={handleChange}
                    className={errors.depositAmount ? "error-input" : ""}
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
                {errors.depositAmount && (
                  <small className="error-message">{errors.depositAmount}</small>
                )}
              </div>
            </div>

            <div className="product-picker-card vendor-product-preference-card">
              <div className="vendor-product-preference-header">
                <div>
                  <h3>Product Preference</h3>
                  <p>Add multiple products with separate Inward / Outward opening quantities.</p>
                </div>
                <span className="vendor-product-count">{formData.products?.length || 0} Added</span>
              </div>

              <div className="product-picker vendor-product-picker">
                <div className="form-group">
                  <label className="form-label">Product Name</label>
                  <div className="vendor-product-dropdown" ref={productMenuRef}>
                    <button type="button" className="vendor-product-dropdown-button" onClick={() => setIsProductMenuOpen((open) => !open)}>
                      <span className={productSearchTerm ? "has-value" : ""}>{productSearchTerm || "Search product..."}</span>
                      <span className={`vendor-product-arrow ${isProductMenuOpen ? "open" : ""}`}>▼</span>
                    </button>
                    {isProductMenuOpen && <div className="vendor-product-dropdown-menu">
                      <input type="text" className="vendor-product-search" placeholder="Type to filter…" value={productSearchTerm} onChange={(e) => { setProductSearchTerm(e.target.value); setSelectedProductId(null); }} autoFocus onClick={(e) => e.stopPropagation()} />
                      <div className="vendor-product-option-list">
                        {loadingProducts ? <div className="vendor-product-empty">Loading products…</div> : availableProducts.length === 0 ? <div className="vendor-product-empty">No matching products</div> : availableProducts.map((product) => (
                          <button key={product.id} type="button" className="vendor-product-option" onClick={() => handleSelectProduct(product)}><span>{product.name}</span>{String(selectedProductId) === String(product.id) && <span>✓</span>}</button>
                        ))}
                      </div>
                    </div>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Action Type</label>
                  <select className="form-input vendor-product-action-select" value={newRowActionType} onChange={(e) => setNewRowActionType(e.target.value)}>
                    <option value="Inward">Inward</option><option value="Outward">Outward</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Opening Qty</label>
                  <input type="text" min="0" step="1" className="form-input quantity-input" placeholder="0" value={newRowOpeningQty} onChange={(e) => setNewRowOpeningQty(e.target.value)} onWheel={(e) => e.target.blur()} />
                </div>

                <div className="form-group" style={{ alignSelf: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn-add-row vendor-add-row vendor-add-product"
                    onClick={handleAddProductRow}
                  >
                    <span className="btn-icon">+</span> Add Product
                  </button>
                </div>
              </div>

              <div className="product-rows vendor-product-rows">
                {!formData.products?.length ? (
                  <div className="empty-state">No product preferences added yet. Select a product above and click Add Product.</div>
                ) : (
                  formData.products.map((product, index) => (
                  <div className="product-row vendor-product-row" key={`${product.id}-${index}`}>
                    <div className="vendor-product-row-name" title={product.name}>{product.name}</div>
                    <select className="form-input vendor-product-row-action" value={product.actionType || "Inward"} onChange={(e) => handleProductPreferenceChange(index, "actionType", e.target.value)}>
                      <option value="Inward">Inward</option><option value="Outward">Outward</option>
                    </select>
                    <input type="text" min="0" step="1" className="form-input quantity-input product-row-qty" value={product.openingQty ?? 0} onChange={(e) => handleProductPreferenceChange(index, "openingQty", Number(e.target.value) || 0)} onWheel={(e) => e.target.blur()} />
                    <button type="button" className="product-row-remove vendor-product-remove" onClick={() => handleRemoveProductRow(index)} aria-label={`Remove ${product.name}`}>✕</button>
                  </div>
                  ))
                )}
              </div>
            </div>
          </section>

          {/* FORM ACTIONS */}
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={handleCancel}
            >
              Cancel
            </button>

            <button type="submit" className="btn btn-primary">
              {editMode ? "Update Vendor" : "Save Vendor"}
            </button>
          </div>
        </form>
      </div>

      <CustomAlert
        isOpen={customAlert.isOpen}
        onClose={() => setCustomAlert({ isOpen: false, message: '', type: 'info' })}
        message={customAlert.message}
        type={customAlert.type}
      />
    </div>
  );
}

export default VendorForm;